import 'moment/dist/locale/pt-br';
import 'moment/locale/pt-br';
import ReactDOM from 'react-dom/client';
import { Providers } from './providers/index.tsx';
import { MainRoutes } from './routes.tsx';
import './styles/global.css';
import './i18n';
import * as Sentry from '@sentry/react';
import Clarity from '@microsoft/clarity';

const isSentryEnabled =
    import.meta.env.VITE_SENTRY_ENABLED !== 'false' &&
    Boolean(import.meta.env.VITE_SENTRY_DSN);

const parseSampleRate = (value: string | undefined, fallback: number) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
};

const traceTargets: Array<string | RegExp> = [
    'localhost',
    /^https:\/\/surfland-store\.trilan\.app\/api/,
];

if (import.meta.env.VITE_API_URL) {
    traceTargets.push(import.meta.env.VITE_API_URL);
}

if (import.meta.env.VITE_GATEWAY_URL) {
    traceTargets.push(import.meta.env.VITE_GATEWAY_URL);
}

if (isSentryEnabled) {
    const defaultTracesSampleRate = import.meta.env.PROD ? 0.2 : 1.0;
    const defaultProfilesSampleRate = import.meta.env.PROD ? 0.1 : 1.0;
    const defaultReplaysSessionSampleRate = import.meta.env.PROD ? 0.05 : 0.1;
    const defaultReplaysOnErrorSampleRate = 1.0;

    // Funções para sanitizar dados sensíveis (LGPD compliance)
    const sanitizeEmail = (email: string): string => {
        const [local, domain] = email.split('@');
        const sanitized = local.substring(0, 2) + '*'.repeat(Math.max(0, local.length - 2));
        return `${sanitized}@${domain}`;
    };

    const sanitizeCPF = (cpf: string): string => {
        const clean = cpf.replace(/\D/g, '');
        if (clean.length !== 11) return cpf;
        return `***.***.***-${clean.slice(-2)}`;
    };

    const sanitizePhoneNumber = (phone: string): string => {
        const clean = phone.replace(/\D/g, '');
        return `***-****-${clean.slice(-2)}`;
    };

    const sanitizeData = (obj: any): any => {
        if (!obj) return obj;
        if (typeof obj !== 'object') return obj;
        if (Array.isArray(obj)) return obj.map(sanitizeData);

        const sanitized = { ...obj };
        
        // Padrões de chaves que contêm dados sensíveis
        const sensitiveKeyPatterns = [
            /email/i,
            /cpf/i,
            /document/i,
            /phone/i,
            /cellphone/i,
            /telephone/i,
            /card/i,
            /credit/i,
            /token/i,
            /password/i,
            /token/i,
            /authorization/i,
            /bearer/i,
        ];

        for (const [key, value] of Object.entries(sanitized)) {
            if (typeof value === 'string') {
                // Sanitiza email
                if (/email/i.test(key) && value.includes('@')) {
                    sanitized[key] = sanitizeEmail(value);
                }
                // Sanitiza CPF (formato xxx.xxx.xxx-xx ou sem máscara)
                else if (/cpf|document/i.test(key) && /^\d{3}\.?\d{3}\.?\d{3}-?\d{2}$/.test(value)) {
                    sanitized[key] = sanitizeCPF(value);
                }
                // Sanitiza telefone
                else if (/phone|cellphone|telephone/i.test(key) && /^\D?\d{10,11}/.test(value)) {
                    sanitized[key] = sanitizePhoneNumber(value);
                }
                // Remove conteúdo suspeito para chaves sensíveis
                else if (sensitiveKeyPatterns.some(pattern => pattern.test(key))) {
                    sanitized[key] = '[REDACTED]';
                }
            } else if (typeof value === 'object') {
                sanitized[key] = sanitizeData(value);
            }
        }

        return sanitized;
    };

    Sentry.init({
        dsn: import.meta.env.VITE_SENTRY_DSN || '',
        integrations: [
            Sentry.browserTracingIntegration(),
            Sentry.replayIntegration(),
        ],
        environment: import.meta.env.VITE_SENTRY_ENV || import.meta.env.MODE,
        release: import.meta.env.VITE_SENTRY_RELEASE || '',
        tracesSampleRate: parseSampleRate(
            import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE,
            defaultTracesSampleRate
        ),
        tracePropagationTargets: traceTargets,
        profilesSampleRate: parseSampleRate(
            import.meta.env.VITE_SENTRY_PROFILES_SAMPLE_RATE,
            defaultProfilesSampleRate
        ),
        replaysSessionSampleRate: parseSampleRate(
            import.meta.env.VITE_SENTRY_REPLAYS_SESSION_SAMPLE_RATE,
            defaultReplaysSessionSampleRate
        ),
        replaysOnErrorSampleRate: parseSampleRate(
            import.meta.env.VITE_SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE,
            defaultReplaysOnErrorSampleRate
        ),
        // Hook que sanitiza dados antes de enviar ao Sentry (LGPD compliance)
        beforeSend(event) {
            if (event.request) {
                event.request = sanitizeData(event.request);
            }
            if (event.contexts) {
                event.contexts = sanitizeData(event.contexts);
            }
            if (event.extra) {
                event.extra = sanitizeData(event.extra);
            }
            if (event.tags) {
                event.tags = sanitizeData(event.tags);
            }
            if (event.breadcrumbs) {
                event.breadcrumbs = event.breadcrumbs.map(breadcrumb => ({
                    ...breadcrumb,
                    data: breadcrumb.data ? sanitizeData(breadcrumb.data) : undefined,
                }));
            }
            return event;
        },
    });
}


    if (import.meta.env.DEV && isSentryEnabled) {
        (window as any).Sentry = Sentry;
    }

const clarityProjectId = import.meta.env.VITE_CLARITY_PROJECT_ID;
if (clarityProjectId) {
    Clarity.init(clarityProjectId);
}

ReactDOM.createRoot(document.getElementById('root')!).render(
    <Providers>
        <MainRoutes />
    </Providers>
);
